import { useState } from 'react';
import { Anchor, Group, Loader, Select, Stack, Table } from '@mantine/core';
import { IconClipboardCheck } from '@tabler/icons-react';
import { Link } from '@tanstack/react-router';
import { STOCK_COUNT_STATUSES } from '@zebra/core';
import { DocStatusBadge } from '../../../components/DocStatusBadge';
import { EmptyState } from '../../../components/EmptyState';
import { PageHeader } from '../../../components/PageHeader';
import { useSites, useStockCounts } from '../api';
import dayjs from '../dayjs';
import { CreateCountButton } from './CreateCountButton';

/** Count register (BUILD_SPEC.md §8.6's "Count register"), filterable by site
 * and status — the same fast-filter register pattern §11 asks every module for.
 *
 * Owns its own PageHeader (guidance-wave gap, 2026-08-26): this used to live in
 * the route file (`silo-counts/counts/index.tsx`) with its own `<CreateCountButton
 * />` action, while THIS component's EmptyState rendered a second, independent
 * `<CreateCountButton />` — the route can't see whether the register is empty,
 * so both always rendered, giving a fresh (empty) tenant two "New count"
 * buttons on one screen (Playwright strict-mode failure on the stocktake e2e
 * test). Colocating the header here — matching every sibling register's
 * pattern (PurchaseOrderRegister, SupplierRegister, etc. all own their
 * PageHeader) — lets it hide its action under the exact same emptiness
 * condition the EmptyState below checks. */
export function StockCountsRegister() {
  const [siteId, setSiteId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const sitesQuery = useSites();
  const countsQuery = useStockCounts({ siteId: siteId ?? undefined, status: status ?? undefined });
  const sites = sitesQuery.data?.items ?? [];
  const sitesById = new Map(sites.map((s) => [s.id, s]));
  const rows = countsQuery.data?.items ?? [];
  const showHeaderCreate = countsQuery.isLoading || rows.length > 0;

  return (
    <Stack gap="lg">
      <PageHeader
        title="Stock counts"
        description="Run a physical stocktake and reconcile it against the ledger."
        actions={showHeaderCreate ? <CreateCountButton /> : undefined}
      />
      <Group align="flex-start" gap="xl" wrap="wrap">
        <Group mb="sm">
          <Select
            placeholder="All sites"
            clearable
            data={sites.map((s) => ({ value: s.id, label: s.name }))}
            value={siteId}
            onChange={setSiteId}
            w={200}
          />
          <Select
            placeholder="All statuses"
            clearable
            data={STOCK_COUNT_STATUSES.map((s) => ({ value: s, label: s.replace('_', ' ') }))}
            value={status}
            onChange={setStatus}
            w={200}
          />
        </Group>

        {countsQuery.isLoading ? (
          <Loader size="sm" />
        ) : (
          <Table.ScrollContainer minWidth={560} w="100%">
            <Table verticalSpacing="xs" highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Number</Table.Th>
                  <Table.Th>Site</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((count) => (
                  <Table.Tr key={count.id}>
                    <Table.Td>{count.number}</Table.Td>
                    <Table.Td>{sitesById.get(count.siteId)?.name ?? count.siteId}</Table.Td>
                    <Table.Td>
                      <DocStatusBadge status={count.status} domain="stockCount" />
                    </Table.Td>
                    <Table.Td>{dayjs(count.createdAt).format('DD MMM YYYY HH:mm')}</Table.Td>
                    <Table.Td>
                      {/* Bare `Link` (not Mantine's `component={Link}` polymorphism) —
                       * see SiloStatusCard.tsx's header comment for why `params` needs
                       * this to typecheck. */}
                      <Link to="/silo-counts/counts/$countId" params={{ countId: count.id }}>
                        <Anchor component="span" size="sm">
                          Open
                        </Anchor>
                      </Link>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            {rows.length === 0 && (
              <EmptyState
                icon={IconClipboardCheck}
                title="No stock counts yet"
                hint="Start a physical stocktake to reconcile counted quantities against the ledger."
                action={<CreateCountButton />}
              />
            )}
          </Table.ScrollContainer>
        )}
      </Group>
    </Stack>
  );
}
